import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, User, ArrowRight } from "lucide-react";
import { useState } from "react";
import heroBackground from "@/assets/hero-background.jpg";

interface HeroSectionProps {
  onCalculate: (nome: string, dataNascimento: string) => void;
  loading: boolean;
  errors: { nome?: string; data?: string };
  setErrors: (errors: { nome?: string; data?: string }) => void;
}

const HeroSection = ({ onCalculate, loading, errors, setErrors }: HeroSectionProps) => {
  const [nome, setNome] = useState("");
  const [dia, setDia] = useState("");
  const [mes, setMes] = useState("");
  const [ano, setAno] = useState("");

  const meses = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  const handleSubmit = () => {
    if (dia && mes && ano) {
      const mesNumero = meses.indexOf(mes) + 1;
      const dataFormatada = `${ano}-${mesNumero.toString().padStart(2, '0')}-${dia.padStart(2, '0')}`;
      onCalculate(nome, dataFormatada);
    } else {
      onCalculate(nome, "");
    }
  };

  return (
    <section className="relative overflow-hidden min-h-screen flex items-center justify-center pt-20">
      {/* Background Image with optimized loading */}
      <picture className="absolute inset-0 z-0">
        <img
          src={heroBackground}
          alt="Hero background"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
      </picture>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70 z-[1]" />
      
      <div className="container mx-auto px-4 sm:px-6 md:px-8 text-center relative z-10 max-w-5xl">
        <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-6 sm:p-8 md:p-10 lg:p-12 max-w-6xl mx-auto">
          <div className="animate-fade-in-up mb-8 sm:mb-10 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 text-white leading-tight">
              🔮 Descubra o <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">Segredo Escondido</span> no Seu Nome
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-8 sm:mb-10 md:mb-12 max-w-4xl mx-auto leading-relaxed">
              Seu nome carrega energias que podem estar travando suas finanças, sua saúde e até seus relacionamentos. 
              <br className="hidden md:block" />
              <span className="text-yellow-400 font-semibold">Descubra agora se você está vibrando prosperidade ou bloqueios!</span>
            </p>
          </div>

          {/* Formulário */}
          <div className="animate-fade-in-up delay-300">
            <Card className="bg-black/30 backdrop-blur-sm border-white/20 max-w-3xl mx-auto">
              <CardHeader className="text-center pb-4 sm:pb-6">
                <CardTitle className="text-xl sm:text-2xl text-white">
                  Análise Gratuita do Seu Nome
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nome" className="text-white flex items-center gap-2 justify-start text-sm sm:text-base">
                      <User className="h-4 w-4" />
                      Nome Completo
                    </Label>
                    <Input
                      id="nome"
                      placeholder="Seu nome completo de nascimento"
                      value={nome}
                      onChange={(e) => {
                        setNome(e.target.value);
                        if (errors.nome) setErrors({ ...errors, nome: undefined });
                      }}
                      className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-11 sm:h-12 text-sm sm:text-base"
                    />
                    {errors.nome && <p className="text-red-400 text-xs sm:text-sm">{errors.nome}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2 justify-start text-sm sm:text-base">
                      <Calendar className="h-4 w-4" />
                      Data de Nascimento
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Input
                          placeholder="Dia"
                          value={dia}
                          onChange={(e) => {
                            setDia(e.target.value);
                            if (errors.data) setErrors({ ...errors, data: undefined });
                          }}
                          className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-11 sm:h-12 text-sm sm:text-base"
                          maxLength={2}
                        />
                      </div>
                      <div>
                        <Select value={mes} onValueChange={(value) => {
                          setMes(value);
                          if (errors.data) setErrors({ ...errors, data: undefined });
                        }}>
                          <SelectTrigger className="bg-white/10 border-white/30 text-white h-11 sm:h-12 text-sm sm:text-base">
                            <SelectValue placeholder="Mês" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border z-50">
                            {meses.map((mesNome, index) => (
                              <SelectItem key={index} value={mesNome} className="text-foreground">
                                {mesNome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Input
                          placeholder="Ano"
                          value={ano}
                          onChange={(e) => {
                            setAno(e.target.value);
                            if (errors.data) setErrors({ ...errors, data: undefined });
                          }}
                          className="bg-white/10 border-white/30 text-white placeholder:text-gray-400 h-11 sm:h-12 text-sm sm:text-base"
                          maxLength={4}
                        />
                      </div>
                    </div>
                    {errors.data && <p className="text-red-400 text-xs sm:text-sm">{errors.data}</p>}
                  </div>
                </div>
                
                <div className="text-center pt-2 sm:pt-4">
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    size="lg"
                    className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-lg h-12 sm:h-14 w-full sm:w-auto sm:min-w-[280px] button-glow"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2 sm:mr-3" />
                        Calculando...
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Revelar Agora Minha Numerologia do Nome</span>
                        <span className="sm:hidden">Revelar Minha Numerologia</span>
                        <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Floating elements - hidden on mobile for better UX */}
      <div className="hidden md:block absolute top-20 left-10 w-20 h-20 bg-yellow-400/10 rounded-full animate-float" style={{ animationDelay: '0s' }} />
      <div className="hidden md:block absolute top-40 right-20 w-16 h-16 bg-blue-400/10 rounded-full animate-float" style={{ animationDelay: '1s' }} />
      <div className="hidden lg:block absolute bottom-20 left-1/4 w-12 h-12 bg-purple-400/10 rounded-full animate-float" style={{ animationDelay: '2s' }} />
    </section>
  );
};

export default HeroSection;